import { FormProvider, useForm } from 'react-hook-form'
import { PartnerFormData, partnerFormSchema } from './schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from '@/helpers'
import { PartnerForm } from '@/components/hiring'
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom'
import { Box, Button, Divider, Typography } from '@mui/material'
import {
  JointPropertyRegime,
  MaritalStatus,
  PartnerPayload,
  Race,
} from '@/types'
import { ReadDealResponse } from '@/types'
import {
  useCreatePartner,
  useReadPartner,
  useUpdatePartner,
  useListPartners,
} from '@/api/accounting-dealer/company'
import { useCallback, useMemo, useState } from 'react'
import { routes } from '@/router'
import { LoadingButton } from '@mui/lab'
import { queryClient } from '@/api/react-query'
import { PartnerFormDialog } from './other-partners/form-dialog'
import { Add } from '@mui/icons-material'
import { OtherPartnersList } from './other-partners'

export const MainPartnerPage = () => {
  const { deal } = useOutletContext<ReadDealResponse>()

  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false)

  const [partnerKeyToEdit, setPartnerKeyToEdit] = useState<string | null>(null)

  const navigate = useNavigate()

  function handleOnSuccess() {
    queryClient.invalidateQueries({
      queryKey: ['partners', deal.company.key],
    })

    navigate(generatePath(routes.hiring.company, { deal_key: deal.key }))
  }

  const handleEditPartner = useCallback((partnerKey: string) => {
    setPartnerKeyToEdit(partnerKey)
    setPartnerDialogOpen(true)
  }, [])

  const handleAddPartner = useCallback(() => {
    setPartnerKeyToEdit(null)
    setPartnerDialogOpen(true)
  }, [])

  const { mutateAsync: createPartner, isPending: creatingPartner } =
    useCreatePartner({
      onSuccess: () => {
        toast.success('Sócio criado com sucesso!')
        handleOnSuccess()
      },
    })

  const { mutateAsync: updatePartner, isPending: updatingPartner } =
    useUpdatePartner({
      onSuccess: () => {
        toast.success('Sócio atualizado com sucesso!')
        handleOnSuccess()
      },
    })

  const { data: partners = [], isLoading: isPartnersLoading } = useListPartners(
    {
      companyKey: deal.company.key,
    },
    { staleTime: 0, gcTime: 0 },
  )

  const mainPartnership = useMemo(() => {
    return partners?.find((partner) => partner.is_main)
  }, [partners])

  const { data: mainPartner, isLoading: isMainPartnerLoading } = useReadPartner(
    {
      partnerKey: mainPartnership?.key || '',
      companyKey: deal.company.key,
    },
    { enabled: !!mainPartnership?.key, staleTime: 0, gcTime: 0 },
  )

  const form = useForm<PartnerFormData>({
    resolver: zodResolver(partnerFormSchema),
    values: {
      profession: mainPartner?.profession || '',
      name: mainPartner?.name || deal.user_display_name || '',
      rg: mainPartner?.rg || '',
      tax_id: mainPartner?.tax_id || deal.deal_lead?.tax_id || '',
      phone: mainPartner?.phone || deal.deal_lead?.phone || '',
      email: mainPartner?.email || deal.deal_lead?.email || '',
      marital_status: mainPartner?.marital_status || '',
      joint_property_regime: mainPartner?.joint_property_regime || '',
      county_of_birth: mainPartner?.county_of_birth || '',
      specializations: [],
      nationality: mainPartner?.nationality || '',
      address: {
        zip_code: mainPartner?.address?.zip_code || '',
        street: mainPartner?.address?.street || '',
        number: mainPartner?.address?.number || '',
        complement: mainPartner?.address?.complement || '',
        district: mainPartner?.address?.district || '',
        ibge_code: mainPartner?.address?.ibge_code || '',
        state_code: mainPartner?.address?.state_code || '',
      },
      crm: mainPartner?.crm || '',
      voter_registration: mainPartner?.voter_registration || '',
      income_tax: mainPartner?.income_tax || '',
      pis: mainPartner?.pis || '',
      has_cnh: mainPartner?.has_cnh ?? '',
      government_employee: mainPartner?.government_employee ?? '',
      contributor_inss: mainPartner?.contributor_inss ?? '',
      is_other_company_partner: mainPartner?.is_other_company_partner ?? '',
      race: mainPartner?.race || '',
    },
  })

  const { handleSubmit } = form

  const handleSubmitPartner = (data: PartnerFormData) => {
    const payload: PartnerPayload = {
      ...data,
      is_main_partner: true,
      race: data.race as Race,
      joint_property_regime: data.joint_property_regime as JointPropertyRegime,
      marital_status: data.marital_status as MaritalStatus,
      has_cnh: data.has_cnh === 'true' || data.has_cnh === true,
      government_employee:
        data.government_employee === 'true' ||
        data.government_employee === true,
      contributor_inss:
        data.contributor_inss === 'true' || data.contributor_inss === true,
      is_other_company_partner:
        data.is_other_company_partner === 'true' ||
        data.is_other_company_partner === true,
    }

    if (mainPartner) {
      updatePartner({
        companyKey: deal.company.key,
        partnerKey: mainPartner.key,
        partnerData: payload,
      })
    } else {
      createPartner({ companyKey: deal.company.key, partnerData: payload })
    }
  }

  const isLoading = isPartnersLoading || isMainPartnerLoading

  return (
    <Box>
      <Typography variant="h3" mb={6} textAlign="center">
        Dados do sócio principal
      </Typography>
      <form onSubmit={handleSubmit(handleSubmitPartner)}>
        <FormProvider {...form}>
          <PartnerForm isMainPartner isLoading={isLoading} />
        </FormProvider>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h3" mb={2} textAlign="center">
          Outros sócios
        </Typography>

        <OtherPartnersList
          partners={partners.filter((partner) => !partner.is_main)}
          onEdit={handleEditPartner}
        />

        <Box textAlign="center">
          <Button
            variant="outlined"
            onClick={handleAddPartner}
            startIcon={<Add />}
            sx={{ mt: 2 }}
          >
            Adicionar sócio
          </Button>
        </Box>

        <LoadingButton
          type="submit"
          sx={{ display: 'block', ml: 'auto', mt: 4 }}
          loading={creatingPartner || updatingPartner}
        >
          Avançar
        </LoadingButton>
      </form>

      <PartnerFormDialog
        open={partnerDialogOpen}
        onClose={() => setPartnerDialogOpen(false)}
        partnerKey={partnerKeyToEdit}
        companyKey={deal.company.key}
      />
    </Box>
  )
}
